import { Button, cn, Dialog, DialogContent, DialogHeader, DialogTitle } from '@kalos/ui';
import { format, parseISO } from 'date-fns';
import { type FC, type ReactNode, useMemo, useState } from 'react';

import { useAuth } from '../../../../../context/AuthContext';
import { useMemoQuery } from '../../../../../hooks/react-query/useMemoQuery';
import { NoteForm } from '../NotesForm/UploadMemoForm';

/**
 * Author initials in the shape the historical Event.logNotes lines use, so live
 * notes and the frozen legacy text below them read as one list: first letter of
 * the first and last words, or the first two letters of a single-word name.
 */
const initialsFromName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatNoteDate = (createdDate: string): string => {
  try {
    return format(parseISO(createdDate), 'MM/dd/yyyy');
  } catch {
    return '';
  }
};

/** Strips the "MM/DD/YYYY " prefix and " -INITIALS" suffix off a mirrored line. */
const MIRRORED_LINE = /^\d{2}\/\d{2}\/\d{4} ([\s\S]*) -\S+$/;

/**
 * Drops legacy lines that repeat a note we are already rendering.
 *
 * Between 2026-08-27 and this change the backend appended each new internal
 * note into Event.logNotes, so notes from that window live in both sources and
 * would otherwise be listed twice. Matching is on the note text alone, because
 * the mirrored line's date and initials were captured at write time and do not
 * necessarily agree with the record now.
 *
 * A note edited during that window left its ORIGINAL wording in the blob, which
 * matches nothing and is kept — so some jobs still show one stale line until
 * the column is cleaned up.
 */
const withoutMirroredLines = (legacy: string, descriptions: Set<string>): string =>
  legacy
    .split('\n')
    .filter((line) => {
      const text = line.match(MIRRORED_LINE)?.[1]?.trim();
      return !text || !descriptions.has(text);
    })
    .join('\n')
    .trim();

/**
 * The job's internal notes, rendered straight from the memo records.
 *
 * This used to read Event.logNotes, a copy of each note's text that the backend
 * appended on create. Nothing rewrote that copy when a note was edited or
 * deleted, so the panel showed stale text (86bc5496u), and the same column is
 * typeable in the PM visit drawer and at job creation — so regenerating it
 * would have erased what people wrote there. Reading the notes removes both
 * problems: there is no second copy to fall out of date or to overwrite.
 *
 * `legacyNotes` is whatever Event.logNotes already holds. It is shown below the
 * live notes, matching the old newest-first ordering, and is now frozen history
 * rather than something that grows.
 */
export const JobNotesContent: FC<{
  jobId: number;
  legacyNotes?: string;
  className?: string;
}> = ({ jobId, legacyNotes, className }) => {
  const { data } = useMemoQuery({
    enabled: jobId > 0,
    filters: {
      eventId: jobId,
      isActive: true,
      isInternal: true,
      orderBy: 'created_date',
      orderDir: 'DESC',
      pageNumber: 0,
      withoutLimit: false,
    },
  });

  const lines = useMemo(
    () =>
      (data?.results ?? [])
        // Photo- and audio-only notes carry no text, so they have nothing to show here.
        .filter((memo) => memo.description?.trim())
        .map(
          (memo) =>
            `${formatNoteDate(memo.createdDate)} ${memo.description.trim()} -${initialsFromName(memo.createdByName ?? '')}`,
        ),
    [data?.results],
  );

  const legacy = useMemo(() => {
    const trimmed = legacyNotes?.trim() ?? '';
    if (!trimmed) return '';
    const descriptions = new Set(
      (data?.results ?? []).map((memo) => memo.description?.trim()).filter(Boolean) as string[],
    );
    return withoutMirroredLines(trimmed, descriptions);
  }, [legacyNotes, data?.results]);

  if (lines.length === 0 && !legacy) return null;

  return (
    <div
      className={cn(
        'max-h-24 overflow-y-auto whitespace-pre-wrap break-words rounded border border-gray-100 bg-gray-50 p-2 text-xs text-gray-500 [overflow-wrap:anywhere]',
        className,
      )}
    >
      {lines.join('\n')}
      {lines.length > 0 && legacy ? '\n' : ''}
      {legacy}
    </div>
  );
};

interface JobNotesPreviewProps {
  jobId: number;
  readOnly?: boolean;
  /** Rendered between the section header and the footer. */
  children?: ReactNode;
  /** When provided, renders an "All notes →" link that invokes it. */
  onViewAllNotes?: () => void;
}

export const JobNotesPreview: FC<JobNotesPreviewProps> = ({
  jobId,
  readOnly = false,
  children,
  onViewAllNotes,
}) => {
  const { user } = useAuth();
  const [addOpen, setAddOpen] = useState(false);

  return (
    <>
      <div className="pt-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Job Notes</span>
          {!readOnly && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-xs"
              onClick={() => setAddOpen(true)}
            >
              + Add
            </Button>
          )}
        </div>

        {children && <div className="mt-2">{children}</div>}

        <div className="mt-2 flex items-center justify-between">
          {onViewAllNotes ? (
            <button
              type="button"
              className="text-xs text-blue-600 hover:underline"
              onClick={onViewAllNotes}
            >
              All notes →
            </button>
          ) : (
            <span />
          )}
          <span className="rounded-sm border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-500">
            For Internal Use
          </span>
        </div>
      </div>

      {!readOnly && (
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle>Add Internal Note</DialogTitle>
            </DialogHeader>
            <NoteForm
              jobId={jobId}
              loggedUserId={user.id}
              showCancelButton
              onClose={() => setAddOpen(false)}
              onSuccess={() => setAddOpen(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
